// Which pass of the initial sync is running, and the arithmetic behind the boot progress
// bar: the id ranges each pass covers, how many documents it has to pull, and the counters
// the bar divides by.
//
// These live outside DatabaseWrapper because that class imports pouchdb-browser, which
// references `self` and cannot be loaded under node, so anything that wants a unit test is
// extracted into a module of its own. DatabaseWrapper delegates to them.

/**
 * Which pass of the sync is running.
 *
 * The initial sync is split so the days the task calendar shows arrive before
 * everything else — on a large account the single-pass version left the calendar
 * on half-filled, display-only data for the whole sync.
 *
 * The order is deliberate and the boundary between the two initial passes is
 * load-bearing: `setInitialSyncCompleted(true)` fires only after PriorityWindow,
 * never after CalendarWindow. That flag is what allows computed task statuses to
 * be PERSISTED, and after only the calendar pass the months behind it have not
 * arrived yet — persisting then would write `no_task` over real training days,
 * which is the #4212 cache-poisoning failure. Finishing CalendarWindow means "the
 * calendar has real data to show", not "the sync is done".
 */
export enum SyncPhase {
  /** Pass 1: every non-log doc + logs inside the task calendar's window. */
  CalendarWindow = 'calendar-window',
  /** Pass 2: the rest of the priority window (logs back to the 3-month cutoff). */
  PriorityWindow = 'priority-window',
  /** Continuous sync of everything, including older history. */
  Live = 'live',
}

/** True for the two passes that make up the initial (non-live) sync. */
export function isInitialSyncPhase(phase: SyncPhase): boolean {
  return phase === SyncPhase.CalendarWindow || phase === SyncPhase.PriorityWindow;
}

/**
 * Should this sync pass pay for the boot progress bar's document pre-count?
 *
 * Two conditions, excluding different things. ONLY THE USER DATABASE: no other
 * selector can be re-described as allDocs id ranges, and the bar follows the user
 * database alone (`computeSyncProgressFraction`). ONLY THE FIRST PASS (#4435):
 * nothing reads the priority pass's answer, because the boot screen's progress
 * subscriber is already torn down by the time that pass starts.
 *
 * If something ever DOES watch the bar during the priority pass, this is the one
 * line to change — with no pre-count the total falls back to PouchDB's rough
 * `pending`, a worse estimate rather than a broken one. The measurement and the
 * full argument are in `design-docs/sync-integrity.md`, "Only the user database's
 * FIRST pass is counted".
 */
export function shouldPreCountInitialSyncDocs(phase: SyncPhase, isUserDb: boolean): boolean {
  return phase === SyncPhase.CalendarWindow && isUserDb;
}

/**
 * One half-open range of document ids: every id with `startkey <= id < endkey`.
 *
 * `inclusive_end` is part of the range rather than something each caller
 * remembers to add. A half-open rule that lives only in a doc comment and is
 * re-applied at every call site is the same drift risk this helper removes.
 */
export interface DocIdKeyRange {
  startkey: string;
  endkey: string;
  inclusive_end: false;
}

/** Sorts above every document id this app writes — the "end of the keyspace". */
const ID_RANGE_END_SENTINEL = '\ufff0';

/**
 * The id ranges an initial sync pass actually covers, expressed as allDocs key
 * ranges rather than as a Mango selector.
 *
 * MIRRORS the initial-sync selector in `database-wrapper.ts` — search there for
 * `buildInitialSyncIdRanges`, which carries a pointer back to this function.
 * The selector is the authority: it decides what actually replicates, and this
 * builder only re-describes the same id space in a form allDocs can read. If
 * the two disagree the document count is wrong, so edit them together. The
 * selector's id clauses are:
 *
 *     _id < 'log/'  OR  _id >= 'log/<cutoff>'  OR  _id >= 'log/\ufff0'
 *
 * The third clause is redundant here — a cutoff is a date such as `2026-06-01`,
 * which always sorts below `\ufff0`, so `log/<cutoff>` onwards already contains
 * everything at or after `log/\ufff0`. That leaves exactly two ranges, which is
 * two allDocs reads rather than three.
 *
 * Why a range form is needed at all: allDocs can count what a pass will bring
 * down without downloading any document bodies, and PouchDB's `pending` number
 * cannot — see `computePullSyncTaskCounters` for what is wrong with `pending`.
 *
 * The ranges do NOT encode the selector's excluded ids — allDocs has no way to
 * express them. `countInitialSyncDocsToPull` applies those while counting.
 */
export function buildInitialSyncIdRanges(logCutoffDate: string): DocIdKeyRange[] {
  return [
    // everything before the log/ namespace: plans, settings, exercises, …
    { startkey: '', endkey: 'log/', inclusive_end: false },
    // the log entries this pass wants, plus every namespace that sorts after log/
    { startkey: `log/${logCutoffDate}`, endkey: ID_RANGE_END_SENTINEL, inclusive_end: false },
  ];
}

/**
 * How many of `remoteIds` this device still has to download.
 *
 * A document already present locally is skipped because replication skips it
 * too: PouchDB asks the target which revisions it is missing (`revs_diff`) and
 * only fetches those. Without the subtraction, an initial sync that was
 * interrupted and is now resuming would count everything it already has and the
 * progress bar would stop well short of full.
 *
 * The subtraction is by id, not by revision, so it is an approximation in one
 * direction: a document held locally at an OLDER revision is counted as "already
 * have" but still transfers. That only makes the total slightly low, and the
 * caller clamps the completed count to the total, so the bar reaches full early
 * rather than overflowing. Getting it exact would mean a revs_diff round trip
 * over every id, which costs more than the bar is worth.
 *
 * `excludedIds` must be exactly the selector's excluded-id list and nothing
 * wider. Skipping every `_design/*` id looks tidier and was tried, but it is a
 * claim the selector does not make: the selector names two design docs and
 * would replicate any other, so a blanket skip undercounts the moment a third
 * design doc exists.
 */
export function countInitialSyncDocsToPull(
  remoteIds: readonly string[],
  localIds: ReadonlySet<string>,
  excludedIds: readonly string[],
): number {
  const excluded = new Set(excludedIds);
  let toPull = 0;
  for (const id of remoteIds) {
    // if the selector excludes this id, it never transfers
    if (excluded.has(id)) continue;
    // if this device already has the document, revs_diff keeps it off the wire
    if (localIds.has(id)) continue;
    toPull += 1;
  }
  return toPull;
}

/** Inputs to {@link computePullSyncTaskCounters}. Named rather than positional
 *  because three same-typed numbers in a row invite a silent transposition. */
export interface PullSyncTaskCounterInput {
  /** documents this pass has read so far, counted from the start of the pass */
  docsDone: number;
  /** PouchDB's `pending` for this batch — used only when there is no real count */
  docsPending: number;
  /** the pre-counted document total, or null while it has not answered */
  knownDocsToPull: number | null;
  /**
   * What `docsDone` read at the instant the pre-count sampled the local
   * database. Both halves of the fraction have to be measured from the same
   * moment. The count subtracts documents this device already holds, and on a
   * slow link the running pass has itself written some of them before the
   * sample — so those documents are missing from the total while still being
   * counted as done. Left uncorrected the bar reaches 100% partway through and
   * then sits frozen for the rest of the sync.
   */
  docsDoneBaseline: number;
}

/**
 * The sync-progress counters for one pull batch: how much work there is in
 * total, and how much of it is done.
 *
 * Two ways to answer, and they differ a lot in quality:
 *
 *  - `knownDocsToPull != null` — the pre-count answered, so the total is a real
 *    document count and the fraction is proportional to the download.
 *  - `knownDocsToPull == null` — fall back to PouchDB's `pending`. THIS IS THE
 *    CANONICAL EXPLANATION of why that number is poor; other files point here
 *    rather than restating it. `pending` counts remaining entries in the
 *    CHANGES FEED, not documents still to download, and the initial sync's
 *    server-side selector filters most of those entries out before they ever
 *    become documents. Measured on a real 16,768-document account: the pull
 *    moved 1,215 documents in total, yet `pending` started at 16,577 and had
 *    collapsed to 232 by the time only 294 documents had moved. A bar built on
 *    it creeps early and races at the end. It is the shape of the estimate
 *    before the pre-count lands, or when the count failed — not a total anyone
 *    should trust.
 *
 * Both forms add 1 to the total for the "finish the pass" step that
 * `endInitialSync` completes, so the bar can never read 100% while documents are
 * still arriving.
 */
export function computePullSyncTaskCounters(
  input: PullSyncTaskCounterInput,
): { syncTasksCount: number; syncTasksCompleted: number } {
  const { docsDone, docsPending, knownDocsToPull, docsDoneBaseline } = input;
  if (knownDocsToPull != null) {
    // measure the numerator from the same moment the total was measured
    const doneSinceBaseline = Math.max(0, docsDone - docsDoneBaseline);
    return {
      syncTasksCount: knownDocsToPull + 1,
      // clamp: the count skips documents this device already holds by id, so a
      // local doc at an older revision still transfers without having been
      // counted. Without the clamp the bar would overshoot 100%.
      syncTasksCompleted: Math.min(doneSinceBaseline, knownDocsToPull),
    };
  }
  return {
    syncTasksCount: docsDone + docsPending + 1,
    syncTasksCompleted: docsDone,
  };
}

